import { DestroyRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, interval, merge } from 'rxjs';
import { catchError, exhaustMap, finalize, startWith } from 'rxjs/operators';
import { AttendanceOverview } from '../../models/AttendanceOverview';
import { AuthService } from '../../services/auth.service';
import { User } from '../../models/User';
import { AttendanceService } from '../../services/attendance.service';
import { ToastService } from '../../services/toast.service';
import { QrChallengeService } from '../../services/qr-challenge.service';
import { QrScanPreview } from '../../models/QrScanPreview';

type ScannerState = 'idle' | 'starting' | 'scanning' | 'validating' | 'preview';

interface DetectedBarcodeLike {
  rawValue?: string;
}

interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcodeLike[]>;
}

interface BarcodeDetectorConstructorLike {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
}

type LegacyQrScannerDecodeResult = string | { data?: string };

interface LegacyQrScannerInstance {
  start(): Promise<void>;
  stop(): void;
  destroy(): void;
}

interface LegacyQrScannerConstructorLike {
  new (
    video: HTMLVideoElement,
    onDecode: (result: LegacyQrScannerDecodeResult) => void,
    options?: {
      preferredCamera?: 'environment' | 'user' | string;
      returnDetailedScanResult?: boolean;
      maxScansPerSecond?: number;
      highlightScanRegion?: boolean;
      highlightCodeOutline?: boolean;
      onDecodeError?: (error: unknown) => void;
    }
  ): LegacyQrScannerInstance;
}

declare global {
  interface Window {
    BarcodeDetector?: BarcodeDetectorConstructorLike;
    QrScanner?: LegacyQrScannerConstructorLike;
  }
}

@Component({
  selector: 'app-qr-scan',
  imports: [],
  template: `
    <section class="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <div class="overflow-hidden rounded-[2rem] bg-gradient-to-br from-emerald-950 via-emerald-800 to-lime-600 p-6 text-white shadow-xl">
        <p class="text-[0.7rem] font-semibold uppercase tracking-[0.35em] text-white/70">
          Profilo attivo
        </p>
        <div class="mt-5 flex items-center gap-4">
          <div class="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 text-2xl font-semibold backdrop-blur-sm">
            {{ initials() }}
          </div>
          <div class="min-w-0">
            <h1 class="truncate text-2xl font-semibold">
              {{ displayName() }}
            </h1>
            <p class="truncate text-sm text-white/80">
              {{ primaryEmail() }}
            </p>
          </div>
        </div>
      </div>

      @if (preview()) {
        <article class="rounded-[2rem] border bg-gradient-to-r p-5 shadow-sm"
                 [class.border-emerald-200]="preview()!.is_valid"
                 [class.from-emerald-50]="preview()!.is_valid"
                 [class.via-white]="preview()!.is_valid"
                 [class.to-lime-50]="preview()!.is_valid"
                 [class.border-rose-200]="!preview()!.is_valid"
                 [class.from-rose-50]="!preview()!.is_valid"
                 [class.via-white]="!preview()!.is_valid"
                 [class.to-amber-50]="!preview()!.is_valid">
          <div class="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
            <div class="max-w-2xl">
              <p class="text-xs font-semibold uppercase tracking-[0.3em]"
                 [class.text-emerald-700]="preview()!.is_valid"
                 [class.text-rose-700]="!preview()!.is_valid">
                Esito timbratura
              </p>
              <h2 class="mt-2 text-2xl font-semibold text-base-content">
                {{ preview()!.is_valid ? 'Timbratura registrata' : 'Questo QR non è valido' }}
              </h2>
              <p class="mt-2 text-sm text-base-content/70">
                {{ previewMessage() }}
              </p>
            </div>

            <div class="badge h-auto px-4 py-3 text-sm font-semibold"
                 [class.badge-success]="preview()!.is_valid"
                 [class.badge-error]="!preview()!.is_valid">
              {{ previewStatusLabel() }}
            </div>
          </div>

          <div class="mt-5 grid gap-3 md:grid-cols-2">
            <div class="rounded-2xl bg-white/80 px-4 py-3">
              <p class="text-xs font-semibold uppercase tracking-[0.25em] text-base-content/50">Progetto</p>
              <p class="mt-2 text-sm font-medium text-base-content">{{ previewProjectName() }}</p>
            </div>

            <div class="rounded-2xl bg-white/80 px-4 py-3">
              <p class="text-xs font-semibold uppercase tracking-[0.25em] text-base-content/50">Utente</p>
              <p class="mt-2 text-sm font-medium text-base-content">{{ previewUserName() }}</p>
            </div>

            <div class="rounded-2xl bg-white/80 px-4 py-3">
              <p class="text-xs font-semibold uppercase tracking-[0.25em] text-base-content/50">Orario</p>
              <p class="mt-2 text-sm font-medium text-base-content">{{ formatDateTime(preview()!.previewed_at) }}</p>
            </div>

            <div class="rounded-2xl bg-white/80 px-4 py-3">
              <p class="text-xs font-semibold uppercase tracking-[0.25em] text-base-content/50">Esito</p>
              <p class="mt-2 text-sm font-medium text-base-content">{{ previewStatusLabel() }}</p>
            </div>
          </div>

          <div class="mt-4 rounded-2xl border border-dashed px-4 py-3 text-sm"
               [class.border-emerald-300]="preview()!.is_valid"
               [class.bg-emerald-50]="preview()!.is_valid"
               [class.text-emerald-950]="preview()!.is_valid"
               [class.border-rose-300]="!preview()!.is_valid"
               [class.bg-rose-50]="!preview()!.is_valid"
               [class.text-rose-950]="!preview()!.is_valid">
            {{ preview()!.is_valid ? 'La timbratura è stata registrata correttamente.' : 'Riprova con il QR del totem o chiedi di mostrarne uno nuovo.' }}
          </div>

          <div class="mt-5 flex flex-col gap-3 sm:flex-row">
            <button class="btn btn-primary sm:min-w-52" type="button" (click)="scanAnotherQr()">
              Inquadra un altro QR
            </button>
          </div>
        </article>
      } @else {
        <article class="rounded-[2rem] border border-emerald-200 bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-6 shadow-sm">
          <div class="max-w-2xl">
            <p class="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-700">Timbratura</p>
            <h2 class="mt-3 text-3xl font-semibold text-base-content">Apri la fotocamera per timbrare</h2>
          </div>

          <div class="mt-5">
            <button class="btn btn-primary btn-lg min-h-16 w-full px-8 text-lg lg:w-auto lg:min-w-64"
                    type="button"
                    [disabled]="scannerState() === 'starting' || scannerState() === 'scanning' || scannerState() === 'validating'"
                    (click)="startScanner()">
              {{ scannerActionLabel() }}
            </button>
          </div>

          @if (scanError() && !scannerOverlayOpen()) {
            <div class="mt-4 rounded-[1.75rem] border border-rose-200 bg-rose-50 p-4 text-sm text-rose-950 shadow-sm">
              {{ scanError() }}
            </div>
          }

          @if (!isSecureContext()) {
            <div class="mt-4 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              La fotocamera non è disponibile da questa pagina. Apri l'app dal link corretto o chiedi supporto.
            </div>
          }
        </article>
      }

      <article class="overflow-hidden rounded-[2rem] border border-emerald-200 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.18),transparent_34%),linear-gradient(135deg,#ffffff_0%,#f7fbf8_55%,#eef7f0_100%)] p-5 shadow-sm">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div class="max-w-2xl">
            <p class="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-700">La tua giornata</p>
            <h2 class="mt-2 text-3xl font-semibold text-slate-900">
              {{ attendanceHeadline() }}
            </h2>
            <p class="mt-2 text-sm text-slate-600">
              {{ attendanceSummary() }}
            </p>
          </div>

          <div class="flex items-center gap-2">
              <div class="badge h-auto px-4 py-3 text-sm font-semibold"
                 [class.badge-success]="isCurrentShiftActive()"
                 [class.badge-neutral]="!isCurrentShiftActive()">
              {{ isCurrentShiftActive() ? 'Al lavoro' : 'Da timbrare' }}
            </div>

            <button class="btn btn-sm border border-slate-200 bg-white/80 text-slate-700 shadow-sm hover:bg-white"
                    type="button"
                    [disabled]="attendanceLoading()"
                    (click)="refreshAttendance()">
              {{ attendanceLoading() ? 'Aggiornamento...' : 'Aggiorna' }}
            </button>
          </div>
        </div>

        @if (attendanceLoading() && !attendanceOverview()) {
          <div class="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(18rem,0.95fr)]">
            <div class="rounded-[1.75rem] bg-slate-950/95 p-5 text-white shadow-xl">
              <div class="h-3 w-28 rounded-full bg-white/15"></div>
              <div class="mt-5 h-14 w-44 rounded-[1.2rem] bg-white/10"></div>
              <div class="mt-5 grid gap-3 sm:grid-cols-2">
                <div class="h-20 rounded-[1.25rem] bg-white/10"></div>
                <div class="h-20 rounded-[1.25rem] bg-white/10"></div>
              </div>
            </div>

            <div class="rounded-[1.75rem] border border-slate-200 bg-white/85 p-5">
              <div class="h-3 w-36 rounded-full bg-slate-200"></div>
              <div class="mt-4 flex flex-col gap-3">
                <div class="h-20 rounded-[1.35rem] bg-slate-100"></div>
                <div class="h-20 rounded-[1.35rem] bg-slate-100"></div>
              </div>
            </div>
          </div>
        } @else {
          <div class="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(18rem,0.95fr)]">
            <div class="rounded-[1.75rem] bg-slate-950 p-5 text-white shadow-xl">
              <p class="text-[0.7rem] font-semibold uppercase tracking-[0.35em] text-white/60">
                Tempo al lavoro
              </p>

              @if (isCurrentShiftActive()) {
                <p class="mt-4 font-mono text-[2.75rem] font-semibold tracking-[-0.04em] text-white sm:text-6xl">
                  {{ elapsedTimeLabel() }}
                </p>

                <div class="mt-5 grid gap-3 sm:grid-cols-2">
                  <div class="rounded-[1.25rem] border border-white/10 bg-white/5 px-4 py-3">
                    <p class="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-white/50">Ingresso</p>
                    <p class="mt-2 text-sm font-medium text-white">{{ formatDateTime(currentShiftClockInAt()) }}</p>
                  </div>

                  <div class="rounded-[1.25rem] border border-white/10 bg-white/5 px-4 py-3">
                    <p class="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-white/50">Sede</p>
                    <p class="mt-2 text-sm font-medium text-white">{{ currentShiftWorkplaceLabel() }}</p>
                  </div>
                </div>

                <p class="mt-4 text-sm text-white/70">
                  Il tempo trascorso si aggiorna automaticamente.
                </p>
              } @else {
                <div class="mt-5 rounded-[1.5rem] border border-white/10 bg-white/5 p-4">
                  <p class="text-xl font-semibold text-white">Nessuna timbratura in corso</p>
                  <p class="mt-2 text-sm text-white/70">
                    Al momento non risultano timbrature aperte.
                  </p>
                </div>
              }

              @if (attendanceUpdatedAt()) {
                <p class="mt-5 text-xs uppercase tracking-[0.3em] text-white/45">
                  Aggiornato alle {{ formatDateTime(attendanceUpdatedAt()) }}
                </p>
              }
            </div>

            <div class="rounded-[1.75rem] border border-slate-200 bg-white/85 p-5 shadow-sm">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Oggi</p>
                  <h3 class="mt-2 text-xl font-semibold text-slate-900">Le tue timbrature di oggi</h3>
                </div>

                <div class="badge badge-outline border-slate-300 px-3 py-3 text-slate-600">
                  {{ completedShifts().length }}
                </div>
              </div>

              @if (completedShifts().length) {
                <div class="mt-4 flex flex-col gap-3">
                  @for (shift of completedShifts(); track shift.shift_id ?? $index) {
                    <div class="rounded-[1.35rem] border border-slate-200 bg-slate-50/90 p-4">
                      <div class="flex items-start justify-between gap-3">
                        <div class="min-w-0">
                          <p class="text-sm font-semibold text-slate-900">
                            {{ formatTimeRange(shift.clock_in_at, shift.clock_out_at) }}
                          </p>
                          <p class="mt-1 text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-slate-500">
                            {{ formatWorkplaceLabel(shift.workplace_label) }}
                          </p>
                        </div>

                        <div class="badge badge-neutral badge-outline whitespace-nowrap">
                          {{ formatDurationMinutes(shift.duration_minutes) }}
                        </div>
                      </div>

                      <p class="mt-3 text-xs text-slate-500">
                        Uscita alle {{ formatDateTime(shift.clock_out_at) }}
                      </p>
                    </div>
                  }
                </div>
              } @else {
                <div class="mt-4 rounded-[1.35rem] border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">
                  Non ci sono ancora timbrature chiuse oggi.
                </div>
              }
            </div>
          </div>
        }

        @if (attendanceError()) {
          <div class="mt-4 rounded-[1.4rem] border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            {{ attendanceError() }}
          </div>
        }
      </article>
    </section>

    <div class="fixed inset-0 z-[80] h-dvh overflow-hidden overscroll-none bg-[#f6f7f1] text-slate-900 transition duration-200"
         [class.pointer-events-none]="!scannerOverlayOpen()"
         [class.opacity-0]="!scannerOverlayOpen()"
         [attr.aria-hidden]="!scannerOverlayOpen()"
         style="padding-top: max(1rem, env(safe-area-inset-top)); padding-bottom: max(1rem, env(safe-area-inset-bottom));">
      <div class="relative h-full overflow-hidden">
        <video #scannerVideo class="absolute inset-0 h-full w-full object-cover" autoplay playsinline muted></video>

        <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(15,23,42,0.03)_0%,_rgba(15,23,42,0.12)_100%)]"></div>
        <div class="pointer-events-none absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-[#f6f7f1] via-[rgba(246,247,241,0.96)] to-transparent"></div>
        <div class="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#f6f7f1] via-[rgba(246,247,241,0.94)] to-transparent"></div>

        <div class="absolute inset-x-0 top-0 flex items-start justify-between gap-3 px-4">
          <div class="max-w-[17rem] rounded-[1.5rem] border border-white/85 bg-white/88 px-4 py-3 shadow-[0_18px_40px_rgba(15,23,42,0.12)] backdrop-blur-md">
            <p class="text-[0.7rem] font-semibold uppercase tracking-[0.35em] text-slate-500">Fotocamera</p>
            <h2 class="mt-1 text-xl font-semibold text-slate-900">
              {{ scannerOverlayTitle() }}
            </h2>
            <p class="mt-1 hidden text-sm text-slate-600 sm:block">
              {{ scannerHint() }}
            </p>
          </div>

          <button class="btn btn-circle border border-white/85 bg-white/88 text-slate-700 shadow-md backdrop-blur-md hover:bg-white"
                  type="button"
                  aria-label="Chiudi scanner"
                  (click)="closeScannerOverlay(scannerState() === 'starting' || scannerState() === 'scanning')">
            ✕
          </button>
        </div>

        @if (scannerState() === 'scanning' || scannerState() === 'starting' || scannerState() === 'validating') {
          <div class="pointer-events-none absolute left-1/2 top-[48%] h-[min(66vw,22rem)] w-[min(66vw,22rem)] max-h-[18rem] max-w-[18rem] -translate-x-1/2 -translate-y-1/2 rounded-[2.25rem] border-2 border-white/95 shadow-[0_0_0_9999px_rgba(15,23,42,0.36)] sm:max-h-[22rem] sm:max-w-[22rem]"></div>
          <div class="pointer-events-none absolute left-1/2 top-[48%] h-px w-[min(54vw,16rem)] max-w-[14rem] -translate-x-1/2 -translate-y-1/2 bg-emerald-300 shadow-[0_0_18px_rgba(110,231,183,0.9)]"></div>
        } @else {
          <div class="absolute inset-0 flex items-center justify-center px-6 text-center">
            <div class="max-w-sm rounded-[2rem] border border-white/85 bg-white/90 px-5 py-6 text-slate-900 shadow-[0_18px_40px_rgba(15,23,42,0.12)] backdrop-blur">
              <p class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Fotocamera</p>
              <h3 class="mt-3 text-2xl font-semibold text-slate-900">Pronto quando vuoi</h3>
              <p class="mt-3 text-sm text-slate-600">
                Apri la fotocamera e inquadra il QR del totem.
              </p>
            </div>
          </div>
        }

        @if (scanError()) {
          <div class="absolute inset-x-4 bottom-24 rounded-[1.5rem] border border-rose-300/70 bg-white/92 px-4 py-3 text-sm text-rose-800 shadow-lg backdrop-blur-md">
            {{ scanError() }}
          </div>
        }

        <div class="absolute inset-x-4 bottom-4">
          <div class="rounded-[1.5rem] border border-white/85 bg-white/90 px-4 py-3 shadow-[0_18px_40px_rgba(15,23,42,0.14)] backdrop-blur-md">
            <div class="flex items-center justify-between gap-3">
              <div class="min-w-0">
                <p class="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-slate-500">Stato fotocamera</p>
                <p class="mt-1 text-sm font-semibold text-slate-900">
                  {{ scannerCompactStatus() }}
                </p>
              </div>

              <div class="shrink-0 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                {{ scannerStateLabel() }}
              </div>
            </div>

            <div class="mt-3 flex gap-2">
              @if (scannerState() === 'scanning' || scannerState() === 'starting' || scannerState() === 'validating') {
                <button class="btn btn-sm btn-outline flex-1 border-slate-300 text-slate-700 hover:bg-slate-100"
                        type="button"
                        (click)="stopScanner(true)">
                  Ferma
                </button>
              } @else {
                <button class="btn btn-sm btn-primary flex-1" type="button" (click)="startScanner()">
                  Apri
                </button>
              }

              <button class="btn btn-sm btn-ghost flex-1 text-slate-700 hover:bg-slate-100"
                      type="button"
                      (click)="closeScannerOverlay(false)">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: ``
})
export class QrScanComponent implements OnInit, OnDestroy {
  private static qrScannerScriptPromise: Promise<LegacyQrScannerConstructorLike> | null = null;
  private readonly defaultThemeColor = '#f6f7f1';
  private readonly scannerThemeColor = '#fbfbf8';

  private auth = inject(AuthService);
  private attendanceService = inject(AttendanceService);
  private destroyRef = inject(DestroyRef);
  private toast = inject(ToastService);
  private qrChallengeService = inject(QrChallengeService);
  private attendanceRefresh$ = new Subject<void>();
  private detector: BarcodeDetectorLike | null = null;
  private legacyScanner: LegacyQrScannerInstance | null = null;
  private stream: MediaStream | null = null;
  private frameId: number | null = null;
  private attendanceFollowUpRefreshId: number | null = null;
  private handlingDetection = false;

  @ViewChild('scannerVideo') private scannerVideo?: ElementRef<HTMLVideoElement>;

  public user = signal<User | null>(this.auth.getUser());
  public preview = signal<QrScanPreview | null>(null);
  public scannerState = signal<ScannerState>('idle');
  public scannerOverlayOpen = signal<boolean>(false);
  public scanError = signal<string | null>(null);
  public attendanceOverview = signal<AttendanceOverview | null>(null);
  public attendanceLoading = signal<boolean>(true);
  public attendanceError = signal<string | null>(null);
  public elapsedSeconds = signal<number>(0);
  public isSecureContext = signal<boolean>(typeof window !== 'undefined' ? window.isSecureContext : false);
  public currentShift = computed(() => this.attendanceOverview()?.current_shift ?? null);
  public completedShifts = computed(() => this.attendanceOverview()?.recent_completed_shifts ?? []);
  public isCurrentShiftActive = computed(() => this.currentShift()?.is_active ?? false);
  public currentShiftClockInAt = computed(() => this.currentShift()?.clock_in_at ?? null);
  public currentShiftWorkplaceLabel = computed(() => this.formatWorkplaceLabel(
    this.currentShift()?.workplace_label ?? null
  ));
  public attendanceUpdatedAt = computed(() => this.attendanceOverview()?.server_time ?? null);
  public attendanceHeadline = computed(() => this.isCurrentShiftActive() ? 'Sei al lavoro' : 'Nessuna timbratura in corso');
  public attendanceSummary = computed(() => {
    if (this.attendanceError() && !this.attendanceOverview()) {
      return 'Non riesco ad aggiornare lo stato in questo momento. Puoi comunque timbrare con il QR.';
    }

    if (this.isCurrentShiftActive()) {
      return 'Hai già timbrato l’ingresso. Qui vedi da quanto tempo sei al lavoro.';
    }

    return 'Al momento non risultano timbrature in corso.';
  });
  public elapsedTimeLabel = computed(() => this.formatElapsedTime(this.elapsedSeconds()));
  public displayName = computed(() => {
    const user = this.user();
    if (!user) {
      return 'Utente non disponibile';
    }

    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    return fullName || user.name || 'Utente senza nome';
  });
  public primaryEmail = computed(() => {
    const user = this.user();
    return user?.email || user?.login_email || 'Email non disponibile';
  });
  public initials = computed(() => {
    return this.displayName()
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'PV';
  });
  public scannerActionLabel = computed(() => {
    switch (this.scannerState()) {
      case 'starting':
        return 'Apertura fotocamera...';
      case 'scanning':
        return 'Fotocamera attiva';
      case 'validating':
        return 'Registrazione...';
      default:
        return 'Apri fotocamera';
    }
  });
  public scannerHint = computed(() => {
    switch (this.scannerState()) {
      case 'starting':
        return 'Consenti l’uso della fotocamera sul telefono.';
      case 'scanning':
        return 'Inquadra il QR del totem nel riquadro.';
      case 'validating':
        return 'QR letto. Sto registrando la timbratura.';
      default:
        return 'La fotocamera resterà aperta finché non inquadri il QR del totem.';
    }
  });
  public scannerOverlayTitle = computed(() => {
    switch (this.scannerState()) {
      case 'starting':
        return 'Consenti la fotocamera';
      case 'scanning':
        return 'Inquadra il QR del totem';
      case 'validating':
        return 'Registrazione in corso';
      default:
        return this.scanError() ? 'Problema con la fotocamera' : 'Fotocamera pronta';
    }
  });
  public scannerCompactStatus = computed(() => {
    switch (this.scannerState()) {
      case 'starting':
        return 'Consenti la fotocamera.';
      case 'scanning':
        return 'Inquadra il QR del totem.';
      case 'validating':
        return 'Registrazione in corso.';
      default:
        return this.scanError() ? 'Problema con la fotocamera.' : 'Fotocamera pronta.';
    }
  });
  public scannerStateLabel = computed(() => {
    switch (this.scannerState()) {
      case 'starting':
        return 'Avvio';
      case 'scanning':
        return 'Attiva';
      case 'validating':
        return 'Invio';
      case 'preview':
        return 'Esito';
      default:
        return 'Pronta';
    }
  });
  public previewMessage = computed(() => this.formatPreviewMessage(this.preview()));
  public previewStatusLabel = computed(() => this.formatPreviewStatus(this.preview()));
  public previewProjectName = computed(() => this.preview()?.project.name || 'Progetto non disponibile');
  public previewUserName = computed(() => {
    const preview = this.preview();
    if (!preview) {
      return 'Utente non disponibile';
    }

    const fullName = [preview.user.first_name, preview.user.last_name].filter(Boolean).join(' ').trim();
    return fullName || preview.user.name || 'Utente non disponibile';
  });

  ngOnInit(): void {
    merge(this.attendanceRefresh$, interval(30000).pipe(startWith(0)))
      .pipe(
        exhaustMap(() => {
          this.attendanceLoading.set(true);

          return this.attendanceService.getOverview().pipe(
            finalize(() => this.attendanceLoading.set(false)),
            catchError((error) => {
              this.attendanceError.set(this.formatAttendanceErrorMessage(error?.error?.message));
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        this.attendanceOverview.set(response.data);
        this.attendanceError.set(null);
        this.syncElapsedSeconds(response.data);
      });

    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isCurrentShiftActive()) {
          this.elapsedSeconds.update((value) => value + 1);
        }
      });
  }

  async startScanner(): Promise<void> {
    this.setThemeColor(this.scannerThemeColor);
    this.scannerOverlayOpen.set(true);
    this.preview.set(null);
    this.scanError.set(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      this.scanError.set('La fotocamera non è disponibile su questo browser.');
      return;
    }

    if (window.BarcodeDetector) {
      await this.startNativeScanner();
      return;
    }

    await this.startLegacyScanner();
  }

  stopScanner(showMessage = false): void {
    if (this.frameId !== null) {
      window.cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }

    this.handlingDetection = false;
    this.detector = null;
    this.destroyLegacyScanner();

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    const video = this.scannerVideo?.nativeElement;
    if (video) {
      video.pause();
      video.srcObject = null;
    }

    if (this.scannerState() !== 'preview') {
      this.scannerState.set('idle');
    }

    if (showMessage) {
      this.toast.show('Fotocamera chiusa.', 'info');
    }
  }

  closeScannerOverlay(showMessage = false): void {
    this.stopScanner(showMessage);
    this.scannerOverlayOpen.set(false);
    this.setThemeColor(this.defaultThemeColor);
  }

  async scanAnotherQr(): Promise<void> {
    this.preview.set(null);
    this.scanError.set(null);
    await this.startScanner();
  }

  refreshAttendance(): void {
    this.requestAttendanceRefresh();
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('it-IT', {
      dateStyle: 'short',
      timeStyle: 'medium',
    }).format(date);
  }

  formatTimeRange(start: string | null | undefined, end: string | null | undefined): string {
    const formattedStart = this.formatTimeOnly(start);
    const formattedEnd = this.formatTimeOnly(end);

    return `${formattedStart} - ${formattedEnd}`;
  }

  formatDurationMinutes(minutes: number): string {
    if (!Number.isFinite(minutes) || minutes <= 0) {
      return '0m';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes}m`;
    }

    if (remainingMinutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${remainingMinutes}m`;
  }

  formatWorkplaceLabel(workplaceLabel: string | null | undefined): string {
    if (typeof workplaceLabel === 'string' && workplaceLabel.trim() !== '') {
      return workplaceLabel.trim();
    }

    return 'Sede non disponibile';
  }

  ngOnDestroy(): void {
    this.attendanceRefresh$.complete();

    if (this.attendanceFollowUpRefreshId !== null) {
      window.clearTimeout(this.attendanceFollowUpRefreshId);
      this.attendanceFollowUpRefreshId = null;
    }

    this.stopScanner(false);
    this.setThemeColor(this.defaultThemeColor);
  }

  private scanNextFrame(): void {
    if (this.scannerState() !== 'scanning' || !this.detector || !this.scannerVideo) {
      return;
    }

    this.frameId = window.requestAnimationFrame(async () => {
      const video = this.scannerVideo?.nativeElement;
      const detector = this.detector;

      if (!video || !detector || this.scannerState() !== 'scanning') {
        return;
      }

      try {
        if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA && !this.handlingDetection) {
          const codes = await detector.detect(video);
          const qrCode = codes.find((code) => typeof code.rawValue === 'string' && code.rawValue.trim() !== '');

          if (qrCode?.rawValue) {
            await this.handleDetectedToken(qrCode.rawValue.trim());
            return;
          }
        }
      } catch (error) {
        this.stopScanner(false);
        this.scanError.set(this.readCameraError(error));
        return;
      }

      this.scanNextFrame();
    });
  }

  private async handleDetectedToken(token: string): Promise<void> {
    if (this.handlingDetection) {
      return;
    }

    this.handlingDetection = true;
    this.scannerState.set('validating');
    this.teardownCameraFeed();

    this.qrChallengeService.previewToken(token).subscribe({
      next: (response) => {
        this.preview.set(response.data);
        this.scannerState.set('preview');
        this.scannerOverlayOpen.set(false);
        this.setThemeColor(this.defaultThemeColor);

        if (response.data.is_valid) {
          this.requestAttendanceRefresh();
          this.requestAttendanceRefresh(1500);
        }
      },
      error: (error) => {
        this.preview.set({
          is_valid: false,
          validation_status: 'error',
          validation_message: this.formatPreviewErrorMessage(error?.error?.message),
          previewed_at: new Date().toISOString(),
          project: { id: null, name: null },
          challenge: { id: null, status: null, issued_at: null, expires_at: null },
          user: {
            id: this.user()?.id ?? null,
            employee_id: this.user()?.employee_id ?? null,
            name: this.user()?.name ?? null,
            first_name: this.user()?.first_name ?? null,
            last_name: this.user()?.last_name ?? null,
            email: this.user()?.email ?? null,
            login_email: this.user()?.login_email ?? null,
          },
        });
        this.scannerState.set('preview');
        this.scannerOverlayOpen.set(false);
        this.setThemeColor(this.defaultThemeColor);
      },
    });
  }

  private async startNativeScanner(): Promise<void> {
    this.stopScanner(false);
    this.scannerState.set('starting');

    try {
      const barcodeDetectorCtor = window.BarcodeDetector;
      if (!barcodeDetectorCtor) {
        throw new Error('BarcodeDetector non disponibile');
      }

      const BarcodeDetectorCtor: BarcodeDetectorConstructorLike = barcodeDetectorCtor;

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' }
        },
        audio: false,
      });

      const video = this.scannerVideo?.nativeElement;
      if (!video) {
        throw new Error('Elemento video non disponibile');
      }

      video.srcObject = this.stream;
      await video.play();

      this.detector = new BarcodeDetectorCtor({ formats: ['qr_code'] });
      this.handlingDetection = false;
      this.scannerState.set('scanning');
      this.scanNextFrame();
    } catch (error) {
      this.stopScanner(false);
      this.scanError.set(this.readCameraError(error));
    }
  }

  private async startLegacyScanner(): Promise<void> {
    this.stopScanner(false);
    this.scannerState.set('starting');

    try {
      const QrScanner = await this.loadLegacyQrScanner();
      const video = this.scannerVideo?.nativeElement;
      if (!video) {
        throw new Error('Elemento video non disponibile');
      }

      this.legacyScanner = new QrScanner(
        video,
        (result) => {
          if (this.handlingDetection) {
            return;
          }

          const token = typeof result === 'string' ? result : result.data;
          if (typeof token === 'string' && token.trim() !== '') {
            void this.handleDetectedToken(token.trim());
          }
        },
        {
          preferredCamera: 'environment',
          returnDetailedScanResult: true,
          maxScansPerSecond: 20,
          highlightScanRegion: false,
          highlightCodeOutline: false,
          onDecodeError: () => undefined,
        }
      );

      this.handlingDetection = false;
      await this.legacyScanner.start();
      this.scannerState.set('scanning');
    } catch (error) {
      this.stopScanner(false);
      this.scanError.set(this.readLegacyScannerError(error));
    }
  }

  private async loadLegacyQrScanner(): Promise<LegacyQrScannerConstructorLike> {
    if (window.QrScanner) {
      return window.QrScanner;
    }

    if (!QrScanComponent.qrScannerScriptPromise) {
      QrScanComponent.qrScannerScriptPromise = new Promise<LegacyQrScannerConstructorLike>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>('script[data-qr-scanner="legacy"]');

        const handleLoad = () => {
          if (window.QrScanner) {
            resolve(window.QrScanner);
            return;
          }

          QrScanComponent.qrScannerScriptPromise = null;
          reject(new Error('Libreria di scansione QR non disponibile'));
        };

        const handleError = () => {
          QrScanComponent.qrScannerScriptPromise = null;
          reject(new Error('Impossibile caricare la libreria di scansione QR'));
        };

        if (existing) {
          existing.addEventListener('load', handleLoad, { once: true });
          existing.addEventListener('error', handleError, { once: true });
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/qr-scanner@1.4.2/qr-scanner.legacy.min.js';
        script.async = true;
        script.dataset['qrScanner'] = 'legacy';
        script.addEventListener('load', handleLoad, { once: true });
        script.addEventListener('error', handleError, { once: true });
        document.head.appendChild(script);
      });
    }

    return QrScanComponent.qrScannerScriptPromise;
  }

  private teardownCameraFeed(): void {
    this.destroyLegacyScanner();
    this.stopVideoTracks();
  }

  private destroyLegacyScanner(): void {
    if (!this.legacyScanner) {
      return;
    }

    this.legacyScanner.stop();
    this.legacyScanner.destroy();
    this.legacyScanner = null;
  }

  private stopVideoTracks(): void {
    if (!this.stream) {
      return;
    }

    this.stream.getTracks().forEach((track) => track.stop());
    this.stream = null;

    const video = this.scannerVideo?.nativeElement;
    if (video) {
      video.pause();
      video.srcObject = null;
    }
  }

  private readCameraError(error: unknown): string {
    if (error instanceof DOMException) {
      switch (error.name) {
        case 'NotAllowedError':
          return 'Consenti l’uso della fotocamera e riprova.';
        case 'NotFoundError':
          return 'Su questo dispositivo non è disponibile una fotocamera.';
        case 'NotReadableError':
          return 'La fotocamera è già in uso. Chiudi le altre app e riprova.';
        case 'OverconstrainedError':
          return 'Non riesco ad aprire la fotocamera posteriore. Riprova.';
        default:
          return 'Non riesco ad aprire la fotocamera. Riprova.';
      }
    }

    if (error instanceof Error) {
      return 'Non riesco ad aprire la fotocamera. Riprova.';
    }

    return 'Non riesco ad aprire la fotocamera. Riprova.';
  }

  private readLegacyScannerError(error: unknown): string {
    return 'Non riesco ad aprire la fotocamera. Riprova.';
  }

  private formatAttendanceErrorMessage(message: unknown): string {
    if (typeof message === 'string' && message.trim() !== '') {
      if (message.toLowerCase().includes('factorial')) {
        return 'Non riesco ad aggiornare le timbrature in questo momento.';
      }
    }

    return 'Non riesco ad aggiornare le timbrature in questo momento.';
  }

  private formatPreviewStatus(preview: QrScanPreview | null): string {
    if (!preview) {
      return 'In attesa';
    }

    if (preview.is_valid) {
      return 'Confermato';
    }

    switch (preview.validation_status) {
      case 'expired':
        return 'Scaduto';
      case 'used':
        return 'Già usato';
      case 'inactive':
        return 'Non disponibile';
      case 'not_found':
      case 'invalid':
        return 'Non valido';
      default:
        return 'Errore';
    }
  }

  private formatPreviewMessage(preview: QrScanPreview | null): string {
    if (!preview) {
      return '';
    }

    if (preview.is_valid) {
      return 'La timbratura è stata registrata correttamente.';
    }

    switch (preview.validation_status) {
      case 'expired':
        return 'Questo QR è scaduto. Chiedi di mostrarne uno nuovo.';
      case 'used':
        return 'Questo QR è già stato usato. Chiedi di mostrarne uno nuovo.';
      case 'inactive':
        return 'Questo QR non è disponibile in questo momento. Riprova con il QR del totem.';
      case 'not_found':
      case 'invalid':
        return 'Questo QR non è valido. Riprova con il QR del totem.';
      default:
        return this.formatPreviewErrorMessage(preview.validation_message);
    }
  }

  private formatPreviewErrorMessage(message: unknown): string {
    if (typeof message !== 'string' || message.trim() === '') {
      return 'Non riesco a registrare la timbratura in questo momento. Riprova tra poco.';
    }

    const normalizedMessage = message.toLowerCase();

    if (normalizedMessage.includes('factorial')) {
      return 'Non riesco a registrare la timbratura in questo momento. Riprova tra poco.';
    }

    return 'Non riesco a registrare la timbratura in questo momento. Riprova tra poco.';
  }

  private syncElapsedSeconds(overview: AttendanceOverview): void {
    this.elapsedSeconds.set(overview.current_shift.is_active ? overview.current_shift.elapsed_seconds : 0);
  }

  private requestAttendanceRefresh(delayMs = 0): void {
    if (delayMs <= 0) {
      this.attendanceRefresh$.next();
      return;
    }

    if (this.attendanceFollowUpRefreshId !== null) {
      window.clearTimeout(this.attendanceFollowUpRefreshId);
    }

    this.attendanceFollowUpRefreshId = window.setTimeout(() => {
      this.attendanceRefresh$.next();
      this.attendanceFollowUpRefreshId = null;
    }, delayMs);
  }

  private formatElapsedTime(totalSeconds: number): string {
    const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    return [hours, minutes, seconds]
      .map((part) => part.toString().padStart(2, '0'))
      .join(':');
  }

  private formatTimeOnly(value: string | null | undefined): string {
    if (!value) {
      return '--:--';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private setThemeColor(color: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    let themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      document.head.appendChild(themeColorMeta);
    }

    themeColorMeta.content = color;
  }
}
